// The renderer: chunk GPU buffers, streaming, point/EDL/line passes, picking.
import { Camera } from './camera.ts';
import { type GL, Prog, Target, PickTarget, tex } from './gl.ts';
import { type V3, dist, frustumPlanes, sphereInFrustum, clamp } from './math.ts';
import { POINT_VS, POINT_FS, PICK_FS, QUAD_VS, EDL_FS, LINE_VS, LINE_FS, IMG_VS, IMG_FS, IMGW_DEPTH_FS, IMGW_ACC_FS, IMGW_COMP_FS, IMGW_OVER_FS } from './shaders.ts';
import { RECORD, RANGE_STEP, imageBounds, imageStep, levelsWanted, planRequests, chooseEvictions, ease, drawLevels, type ChunkIndex, type Want, type RangeReq } from './lod.ts';
import type { Scene, CloudMeta, CloudIndex, Overlays, CameraPaths } from './format.ts';
import { type Volume, KINDS, MAX_VOLUMES, unitRows } from './volumes.ts';
import { type Rigid, identity, apply, mat4, compose, inverse, isIdentity, parseRigid } from './align.ts';
import { decodeVoxels } from './edit/voxels.ts';
import { asCloud } from './edit/instances.ts';
import * as cache from './cache.ts';
import type { PhotoOp } from './photo/photofilter.ts';

export interface Cloud {
  meta: CloudMeta; idx: CloudIndex; bin: string; chunks: Chunk[];
  visible: boolean; color: V3; file: number;
  T: Rigid; M: Float32Array;     // rigid transform onto the scene frame: an alignment here on top of base
  base: Rigid;                   // published placement (a movable object's transform; else identity)
  frames?: { mats: Float32Array; tex: WebGLTexture | null };   // edit cloud: per-frame E (see shaders.ts uFrameT)
  sel?: boolean;                 // edit cloud: some of its points are selected (state bit 1)
}
export interface Chunk {
  key: number; cloud: Cloud; info: ChunkIndex; ends: number[];
  center: V3; radius: number;
  levels: number; pending: boolean; need: number; draw: number;
  lastUsed: number; priority: number;
  buf: WebGLBuffer | null; vao: WebGLVertexArrayObject | null; cap: number;
  dispSp: number;        // displayed splat spacing, eased toward the drawn level's
  edit?: EditChunk;      // only on the edit cloud's chunks: all levels, kept on the CPU too
}
/** An edit-cloud chunk's records (vertex order: level 0, 1, ...) and per-vertex edit state. */
/** An edit-cloud chunk's records (vertex order: level 0, 1, ...) and per-vertex edit state; fr: the frame
 * indices its points come from, and bc / br its bounding sphere (cloud coords) where those frames' transforms
 * draw it (the alignment tree moves points per frame, so the chunk's own box isn't where it is drawn). */
export interface EditChunk { rec: Uint8Array; state: Uint32Array; sbuf: WebGLBuffer | null; fix: Uint16Array; fbuf: WebGLBuffer | null; dens: Uint16Array; dbuf: WebGLBuffer | null; feat: Uint8Array; ebuf: WebGLBuffer | null; val: Float32Array; vbuf: WebGLBuffer | null; fr: number[]; bc: V3; br: number }
/** of: the solo cloud the lines belong with (shown while it is soloed; null: hidden then). */
/**
 * A camera image drawn on its depth map (IMG_VS): depth in metres per depth pixel (dw x dh, row-major,
 * 0 = none), the depth camera's intrinsics K (fx, fy, cx, cy), the image, and model: camera to world.
 */
export interface ImageMesh {
  id: string; depth: Float32Array; dw: number; dh: number; K: number[]; img: ImageBitmap; model: Float32Array;
  of: string | null;   // an edit cloud's: hidden with it, and when another cloud is solo
  ztex: WebGLTexture | null; itex: WebGLTexture | null;   // uploaded on first draw (and again after a context loss)
  /** Its weight per depth pixel (Image weights shading, mode 10: drawn only then, blended with the others), how selected each is, and those uploaded. */
  weight?: Float32Array | null; sel?: Float32Array | null; wtex?: WebGLTexture | null;
  /** Its exposure gain (RGB, Match exposure; else 1), and its bounds for culling and LoD (lod.ts imageBounds; again when it moves). */
  gain?: V3 | null; bounds?: ReturnType<typeof imageBounds> | undefined;
}

export interface LineSet {
  id: string; data: Float32Array; buf: WebGLBuffer | null; color: [number, number, number, number]; visible: boolean; depth: boolean; of: string | null;
  cloud?: Cloud;   // drawn only while this cloud is, in its colour
  /** RGBA per vertex (in place of color), and its buffer. */
  colors?: Float32Array | null; cbuf?: WebGLBuffer | null;
}

/** A published object's voxel cloud: '~pvox:<object cloud id>'. */
const PUB_VOX = '~pvox:';
/** Data shading: a point not in the data group shown (the attribute's value; shaders.ts aVal). */
const NO_VAL = -3e38;

export const hex2rgb = (h: string): V3 => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16) / 255) as V3;

export class Settings {
  mode = 0;            // 0 rgb, 1 lit, 2 height, 3 cloud, 4 normals, 5 source frame, 6 capture range, 7 image edges, 8 point density, 9 image features, 10 image weights (the weighted photos alone: no points drawn), 11 data group (its values on a ramp)
  selOnly = false;     // just the selected points (edit cloud), in the mode's colours
  cull = false;        // hide points whose normal faces away from the eye
  cullT = 0;           // ...or not enough: cull below this cos(normal, direction to the eye); 0 edge on, >0 grazing ones too
  period = 16;         // mode 5: frames per hue cycle (divides 64)
  rangeMax = 4;        // mode 6: the capture range (m) at the red end of the ramp
  edgeView = 0;        // mode 7: 0 Sobel strength, 1 Canny edges, 2 signed strength (foreground / background)
  densR = 0.05;        // mode 8: count the points within this (m) of each
  densK = 0;           // ...and the red end of its ramp: the measured 98th percentile times 2^densK
  featView = 0;        // mode 9: 0 all, 1 the photo's corners and lines, 2 the depth's jumps and creases, 3 photo against depth
  imgOp: PhotoOp = 'rgb';   // mode 10: the photos as they are, or processed (photo/photofilter.ts)
  imgExpo = false;     // ...with their exposures matched (photo/exposure.ts)
  imgLod = 6;          // camera images: a coarser mesh where its depth pixels are smaller than this (render px)
  gain = 1;
  size = 1;
  edl = false;         // off: it darkens every splat's edge against the splats in front (a black rim per point)
  edlStrength = 0.4;
  edlRadius = 1.4;
  fill = true;         // screen-space gap fill
  fillR = 3;           // its reach in render px
  dprCap = 1.5;
  moveScale = 0.6;     // resolution while the camera moves
  targetPx = 1.6;      // LOD: wanted point spacing in render px
  budget = 4e6;
  volumes: Volume[] = [];
  volShow = false;     // draw hidden points (tinted) instead of dropping them
  volTint = 0.35;      // how strongly align/solid points are tinted
  bg: V3 = [0.09, 0.1, 0.12];
  editShow = 6;        // edit cloud: bit 0 show deleted points, 1 tint classes, 2 tint objects/parts
  voxels = true;       // published objects of a type with a voxel model (scene.json voxels) drawn as it
}

export class Viewer {
  gl!: GL;
  cam = new Camera();
  s = new Settings();
  scene!: Scene;
  base = '';
  clouds: Cloud[] = [];
  chunks: Chunk[] = [];
  lines: LineSet[] = [];
  images: ImageMesh[] = [];
  cameras: CameraPaths = {};
  lost = false;
  dirty = true;
  moving = false;
  frame = 0;
  loadedPts = 0;
  drawnPts = 0;
  drawnImgTris = 0;    // camera images' triangles last frame
  bytesIn = 0;
  floorZ = 0;
  bbox = [-10, -10, 0, 10, 10, 5];
  private pts!: Prog; private pick!: Prog; private edl!: Prog; private line!: Prog;
  private tgt: Target | null = null;
  private ptgt: PickTarget | null = null;
  private quad!: WebGLVertexArrayObject;
  private lineVao!: WebGLVertexArrayObject;
  private img!: Prog;
  private imgw!: { depth: Prog; acc: Prog; over: Prog; comp: Prog };
  /** Image weights: the front surface's depth, and the photos summed there (float when it can be: mean; else alpha-over). */
  private wtgt: { w: number; h: number; dfb: WebGLFramebuffer; depth: WebGLTexture; afb: WebGLFramebuffer; acc: WebGLTexture; mean: boolean } | null = null;
  /** Index buffers (in VAOs) of the image meshes' grids, by "dw x dh". */
  private gridVaos = new Map<string, { vao: WebGLVertexArrayObject; n: number }>();
  private flight = new Map<RangeReq, AbortController>();
  onChange: () => void = () => {};
  /** Scans being edited (by id), each drawn from its unedited build instead of its streamed (derived) clouds. */
  edits = new Map<string, Cloud>();
  editChunks: Chunk[] = [];
  /** Mode 8: the point densities (points within s.densR) at the blue and red ends of the ramp (log scale). */
  densRange: [number, number] = [1, 100];
  /** Mode 11: the data group's values at the blue and red ends of the ramp. */
  valRange: [number, number] = [0, 1];
  /** Data group shading: only the points with values in it drawn (those not in the group too go); null: all */
  valCut: [number, number] | null = null;
  /** Mode 8: the density at the red end of the ramp (the measured one moved by s.densK), above the blue end. */
  densRed() { return Math.max(this.densRange[1] * 2 ** this.s.densK, this.densRange[0] * 1.01); }

  constructor(public canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', { antialias: false, alpha: false, depth: true, powerPreference: 'high-performance' });
    if (!gl) throw new Error('WebGL2 is not available in this browser.');
    this.gl = gl;
    canvas.addEventListener('webglcontextlost', e => { e.preventDefault(); this.contextLost(); });
    canvas.addEventListener('webglcontextrestored', () => { this.initGL(); this.lost = false; this.dirty = true; });
    this.initGL();
  }

  private initGL() {
    const gl = this.gl;
    this.pts = new Prog(gl, POINT_VS, POINT_FS);
    this.pick = new Prog(gl, POINT_VS, PICK_FS);
    this.edl = new Prog(gl, QUAD_VS, EDL_FS);
    this.line = new Prog(gl, LINE_VS, LINE_FS);
    this.img = new Prog(gl, IMG_VS, IMG_FS);
    this.imgw = { depth: new Prog(gl, IMG_VS, IMGW_DEPTH_FS), acc: new Prog(gl, IMG_VS, IMGW_ACC_FS), over: new Prog(gl, IMG_VS, IMGW_OVER_FS), comp: new Prog(gl, QUAD_VS, IMGW_COMP_FS) };
    this.wtgt = null;
    this.gridVaos.clear();
    for (const im of this.images) im.ztex = im.itex = im.wtex = null;
    this.quad = gl.createVertexArray()!;
    this.lineVao = gl.createVertexArray()!;
    this.tgt = this.ptgt = null;
    for (const l of this.lines) { l.buf = null; l.cbuf = null; }
    // non-edit chunks leave attributes 3 and 4 (uint aEdit, aFrame) disabled: their constants must be uint 0
    gl.vertexAttribI4ui(3, 0, 0, 0, 0);
    gl.vertexAttribI4ui(4, 0, 0, 0, 0);
    // a version 1 build's records have no quality bytes: attribute 5 stays disabled, range and edge 0 (unknown)
    gl.vertexAttrib4f(5, 0, 0, 0, 0);
    // point density (edit clouds, once measured): 0 elsewhere (unknown)
    gl.vertexAttrib4f(6, 0, 0, 0, 0);
    // image features (edit clouds, once found): uint 0 elsewhere (unknown)
    gl.vertexAttribI4ui(7, 0, 0, 0, 0);
    // a data group's values (edit clouds, the one shown): NO_VAL (not in it) elsewhere
    gl.vertexAttrib4f(8, NO_VAL, 0, 0, 0);
  }

  private contextLost() {
    this.lost = true;
    for (const ac of this.flight.values()) ac.abort();
    this.flight.clear();
    for (const c of this.chunks) { c.levels = 0; c.pending = false; c.buf = c.vao = null; c.cap = 0; }
    for (const c of this.editChunks) { c.buf = c.vao = c.edit!.sbuf = c.edit!.fbuf = c.edit!.dbuf = c.edit!.ebuf = c.edit!.vbuf = null; }   // re-uploaded from the CPU copy
    for (const c of this.edits.values()) if (c.frames) c.frames.tex = null;
    this.clsTex = this.labTex = null;
    this.loadedPts = 0;
  }

  /**
   * A scene without its streamed clouds (the object page draws only edit clouds): its frame, floor and
   * bounds, so the camera, shading and the edit clouds work.
   */
  useScene(scene: Scene, base = '') {
    this.base = base;
    this.scene = scene;
    this.floorZ = Object.values(scene.frames)[0]?.floorZ ?? 0;
    const bb = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
    for (const m of scene.clouds) for (let i = 0; i < 3; i++) { bb[i] = Math.min(bb[i], m.bbox[i]); bb[i + 3] = Math.max(bb[i + 3], m.bbox[i + 3]); }
    if (bb[0] <= bb[3]) this.bbox = bb;
    this.dirty = true;
  }

  async load(base: string) {
    this.base = base;
    const scene = await cache.fetchFreshJson<Scene>(base + 'scene.json');
    this.scene = scene;
    const fr = Object.values(scene.frames)[0];
    this.floorZ = fr?.floorZ ?? 0;
    const bb = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
    let key = 0;
    const idxs = await Promise.all(scene.clouds.map(c => cache.fetchJson<CloudIndex>(base + c.index)));
    scene.clouds.forEach((meta, file) => {
      const idx = idxs[file];
      const b0 = parseRigid(meta.T) ?? identity();
      const cloud: Cloud = { meta, idx, bin: base + 'clouds/' + idx.bin, chunks: [], visible: true, color: hex2rgb(meta.color), file, T: b0, M: mat4(b0), base: b0 };
      for (let i = 0; i < 3; i++) { bb[i] = Math.min(bb[i], meta.bbox[i]); bb[i + 3] = Math.max(bb[i + 3], meta.bbox[i + 3]); }
      for (const info of idx.chunks) {
        const ends = [0];
        for (const n of info.levelCounts) ends.push(ends[ends.length - 1] + n);
        const center: V3 = [0, 1, 2].map(k => info.origin[k] + info.size[k] / 2) as V3;
        const ch: Chunk = {
          key: key++, cloud, info, ends, center, radius: Math.hypot(...info.size) / 2,
          levels: 0, pending: false, need: 1, draw: 0, lastUsed: 0, priority: 0, buf: null, vao: null, cap: 0, dispSp: 0,
        };
        cloud.chunks.push(ch);
        this.chunks.push(ch);
      }
      this.clouds.push(cloud);
    });
    this.bbox = bb;
    this.buildGrid();
    this.publishedVoxels(scene);
    if (scene.overlays?.file) {
      const ov = await cache.fetchJson<Overlays>(base + scene.overlays.file);
      this.buildOverlays(ov);
    }
    if (scene.cameras) {
      this.cameras = await cache.fetchJson<CameraPaths>(base + scene.cameras);
      for (const id of Object.keys(this.cameras)) this.cameraLines(id);
    }
    // forget what a publish replaced (hashed names: anything not listed now is unused)
    cache.prune(base + 'clouds/', new Set(scene.clouds.flatMap((c, i) => [base + c.index, this.clouds[i].bin])));
    cache.prune(base + 'overlays.', new Set(scene.overlays?.file ? [base + scene.overlays.file] : []));
    cache.prune(base + 'cameras.', new Set(scene.cameras ? [base + scene.cameras] : []));
    this.dirty = true;
  }

  // ── overlays ──────────────────────────────────────────────────────────
  static sectionColors: Record<string, [number, number, number, number]> = {
    '1': [1, 0.25, 0.2, 1], '1.2': [1, 0.85, 0.1, 1], '1.5': [0.25, 0.85, 0.4, 1], '2': [0.25, 0.6, 1, 1],
  };

  /** A line set (pairs of points), in one colour or with colors: RGBA per vertex. */
  setLines(id: string, data: Float32Array, color: [number, number, number, number], visible: boolean, depth = true, of: string | null = null, colors: Float32Array | null = null) {
    let l = this.lines.find(x => x.id === id);
    if (!l) { l = { id, data, buf: null, color, visible, depth, of, colors, cbuf: null }; this.lines.push(l); }
    else {
      if (l.buf) this.gl.deleteBuffer(l.buf);
      if (l.cbuf) this.gl.deleteBuffer(l.cbuf);
      l.buf = null; l.cbuf = null; l.data = data; l.color = color; l.depth = depth; l.of = of; l.colors = colors;
    }
    this.dirty = true;
    return l;
  }

  /** Drop the line sets whose id starts with prefix (freeing their buffers). */
  removeLines(prefix: string) {
    for (const l of this.lines) if (l.id.startsWith(prefix)) { if (l.buf) this.gl.deleteBuffer(l.buf); if (l.cbuf) this.gl.deleteBuffer(l.cbuf); }
    this.lines = this.lines.filter(l => !l.id.startsWith(prefix));
    this.dirty = true;
  }

  /** Draw (or replace) camera image `id` on its depth map. */
  setImage(id: string, m: Omit<ImageMesh, 'id' | 'ztex' | 'itex' | 'wtex'>) {
    this.removeImages(id, true);
    this.images.push({ ...m, id, ztex: null, itex: null, wtex: null });
    this.dirty = true;
  }

  /** Move camera image `id` (camera to world). */
  setImageModel(id: string, model: Float32Array) {
    const im = this.images.find(x => x.id === id);
    if (im) { im.model = model; im.bounds = undefined; this.dirty = true; }
  }

  /** Camera image `id`'s photo replaced (processed again), and its exposure gain (unchanged when undefined). */
  setImagePhoto(id: string, img: ImageBitmap | null, gain?: V3 | null) {
    const im = this.images.find(x => x.id === id);
    if (!im) { img?.close(); return; }
    if (img) {
      if (im.itex) this.gl.deleteTexture(im.itex);
      im.img.close();
      im.img = img; im.itex = null;
    }
    if (gain !== undefined) im.gain = gain;
    this.dirty = true;
  }

  /** Camera image `id`'s weights per depth pixel (Image weights shading), and how selected (0 to 1; unchanged when undefined). */
  setImageWeights(id: string, w: Float32Array | null, sel?: Float32Array | null) {
    const im = this.images.find(x => x.id === id);
    if (!im) return;
    if (im.wtex) this.gl.deleteTexture(im.wtex);
    if (w) im.weight = w;
    if (sel !== undefined) im.sel = sel;
    im.wtex = null; this.dirty = true;
  }

  /** Drop the camera images whose id starts with prefix (or is it, exact), freeing their textures. */
  removeImages(prefix: string, exact = false) {
    const hit = (im: ImageMesh) => exact ? im.id === prefix : im.id.startsWith(prefix);
    for (const im of this.images) if (hit(im)) { this.gl.deleteTexture(im.ztex); this.gl.deleteTexture(im.itex); if (im.wtex) this.gl.deleteTexture(im.wtex); im.img.close(); }
    this.images = this.images.filter(im => !hit(im));
    this.dirty = true;
  }

  private gridVao(w: number, h: number) {
    const key = `${w}x${h}`;
    let g = this.gridVaos.get(key);
    if (!g) {
      const gl = this.gl, ix = new Uint32Array((w - 1) * (h - 1) * 6);
      let k = 0;
      for (let y = 0; y < h - 1; y++) for (let x = 0; x < w - 1; x++) {
        const a = y * w + x, b = a + 1, c = a + w, d = c + 1;
        ix[k++] = a; ix[k++] = c; ix[k++] = b; ix[k++] = b; ix[k++] = c; ix[k++] = d;
      }
      const vao = gl.createVertexArray()!;
      gl.bindVertexArray(vao);
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buf);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, ix, gl.STATIC_DRAW);
      gl.bindVertexArray(null);
      g = { vao, n: ix.length };
      this.gridVaos.set(key, g);
    }
    return g;
  }

  /**
   * The camera images worth drawing this frame: those in view, each with the stride of its mesh (lod.ts imageStep:
   * coarser where its depth pixels are small on screen, coarser still while the camera moves) and that grid.
   */
  private imageDraws(list: ImageMesh[]) {
    const c = this.cam, px = c.pxPerM * this.renderScale(), target = this.s.imgLod * (this.moving ? 2 : 1);
    const out: { im: ImageMesh; step: number; gw: number; gh: number; g: { vao: WebGLVertexArrayObject; n: number } }[] = [];
    for (const im of list) {
      if (im.bounds === undefined) im.bounds = imageBounds(im.depth, im.dw, im.dh, im.K, im.model);
      const b = im.bounds;
      if (!b || !sphereInFrustum(this.planes, b.c, b.r)) continue;
      const d = c.ortho ? 1 : Math.max(dist(c.eye, b.c) - b.r, 0.3);
      const step = imageStep(b.cell, d, px, target), gw = Math.ceil((im.dw - 1) / step) + 1, gh = Math.ceil((im.dh - 1) / step) + 1;
      out.push({ im, step, gw, gh, g: this.gridVao(gw, gh) });
    }
    return out;
  }

  /** Draw camera images (their textures uploaded first) with program p: per image its textures, mesh and uniforms. */
  private drawImageMeshes(p: Prog, draws: ReturnType<Viewer['imageDraws']>, weighted: boolean) {
    const gl = this.gl;
    for (const { im, step, gw, gh, g } of draws) {
      this.uploadImage(im);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, im.ztex);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, im.itex);
      if (weighted) { gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, im.wtex!); }
      gl.uniform2i(p.loc('uSz'), im.dw, im.dh);
      gl.uniform2i(p.loc('uGrid'), gw, gh);
      p.i('uStep', step);
      p.f('uK', im.K[0], im.K[1], im.K[2], im.K[3]);
      p.f('uGain', ...(im.gain ?? [1, 1, 1]));
      p.m('uM', im.model);
      gl.bindVertexArray(g.vao);
      gl.drawElements(gl.TRIANGLES, g.n, gl.UNSIGNED_INT, 0);
      this.drawnImgTris += g.n / 3;
    }
    gl.bindVertexArray(null);
  }

  private drawImages() {
    const gl = this.gl, draws = this.imageDraws(this.shownImages().filter(im => !im.weight));
    if (!draws.length) return;
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.depthMask(true);
    const p = this.img.use();
    const c = this.cam;
    p.m('uVP', c.vp);
    p.f('uEye', ...c.e); p.f('uFwd', ...c.f); p.f('uOrtho', c.ortho ? 1 : 0);
    p.i('uZ', 0); p.i('uImg', 1); p.i('uHasW', 0);
    this.drawImageMeshes(p, draws, false);
    gl.activeTexture(gl.TEXTURE0);
  }

  private shownImages() {
    return this.images.filter(im => (this.solo === null || im.of === this.solo) && !(im.of !== null && this.edits.get(im.of)?.visible === false));
  }

  /** Its textures, on first draw (and again after a context loss). */
  private uploadImage(im: ImageMesh) {
    const gl = this.gl;
    if (im.weight && !im.wtex) {
      const n = im.dw * im.dh, rg = new Float32Array(2 * n);
      for (let i = 0; i < n; i++) { rg[2 * i] = im.weight[i]; rg[2 * i + 1] = im.sel ? im.sel[i] : 0; }
      im.wtex = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, im.wtex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RG32F, im.dw, im.dh, 0, gl.RG, gl.FLOAT, rg);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    }
    if (!im.ztex) {
      im.ztex = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, im.ztex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, im.dw, im.dh, 0, gl.RED, gl.FLOAT, im.depth);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    }
    if (!im.itex) {
      im.itex = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, im.itex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, im.img);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    }
  }

  /**
   * Image weights shading (mode 10): the weighted photos (images with weights), each pixel the mean of the photos
   * on the nearest weighted surface, by weight (shaders.ts IMGW_*); onto the screen (the points aren't drawn).
   */
  private drawWeighted(tw: number, th: number) {
    const gl = this.gl, c = this.cam, selOnly = this.s.selOnly && this.anySel() ? 1 : 0;
    const draws = this.imageDraws(this.shownImages().filter(im => im.weight));   // the same meshes in both passes (the front test is a few cm)
    if (!draws.length) return;
    let t = this.wtgt;
    if (!t || t.w !== tw || t.h !== th) {
      if (t) { gl.deleteFramebuffer(t.dfb); gl.deleteFramebuffer(t.afb); gl.deleteTexture(t.depth); gl.deleteTexture(t.acc); }
      const mean = !!gl.getExtension('EXT_color_buffer_float');
      const depth = tex(gl, tw, th, gl.DEPTH_COMPONENT24, gl.DEPTH_COMPONENT, gl.UNSIGNED_INT, gl.NEAREST);
      const acc = mean ? tex(gl, tw, th, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT, gl.NEAREST) : tex(gl, tw, th, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.NEAREST);
      const dfb = gl.createFramebuffer()!, afb = gl.createFramebuffer()!;
      gl.bindFramebuffer(gl.FRAMEBUFFER, dfb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, depth, 0);
      gl.drawBuffers([gl.NONE]);
      gl.bindFramebuffer(gl.FRAMEBUFFER, afb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, acc, 0);
      if (!mean) console.warn('image weights: no float render targets here: the photos are drawn one over another (alpha: weight) instead of averaged');
      t = this.wtgt = { w: tw, h: th, dfb, depth, afb, acc, mean };
    }
    const draw = (p: Prog) => {
      p.m('uVP', c.vp);
      p.f('uEye', ...c.e); p.f('uFwd', ...c.f); p.f('uOrtho', c.ortho ? 1 : 0);
      p.f('uNF', c.ortho ? 0 : c.near, c.ortho ? c.planDepth : c.far);
      p.i('uZ', 0); p.i('uImg', 1); p.i('uW', 2); p.i('uFront', 3); p.i('uHasW', 1); p.i('uSelOnly', selOnly);
      this.drawImageMeshes(p, draws, true);
    };
    gl.viewport(0, 0, tw, th);
    // the nearest surface with any weight
    gl.bindFramebuffer(gl.FRAMEBUFFER, t.dfb);
    gl.depthMask(true);
    gl.clear(gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LESS);
    gl.disable(gl.BLEND);
    draw(this.imgw.depth.use());
    // every photo on it, summed (or one over another)
    gl.bindFramebuffer(gl.FRAMEBUFFER, t.afb);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.disable(gl.DEPTH_TEST); gl.depthMask(false);
    gl.enable(gl.BLEND);
    if (t.mean) gl.blendFunc(gl.ONE, gl.ONE); else gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.activeTexture(gl.TEXTURE3); gl.bindTexture(gl.TEXTURE_2D, t.depth);
    draw((t.mean ? this.imgw.acc : this.imgw.over).use());
    gl.disable(gl.BLEND);
    // onto the screen, carrying their depth for the lines
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LEQUAL); gl.depthMask(true);
    const p = this.imgw.comp.use();
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, t.acc);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, t.depth);
    p.i('uAcc', 0); p.i('uFront', 1);
    gl.bindVertexArray(this.quad);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
    gl.activeTexture(gl.TEXTURE0);
  }

  /** Scan `id`'s camera path as line set 'cam:<id>', placed with its cloud (and its alignment). */
  private cameraLines(id: string) {
    const p = this.cameras[id], c = this.clouds.find(x => x.meta.id === id);
    if (!p || p.length < 6) return;
    const T = c?.T ?? identity(), a = new Float32Array(2 * p.length - 6);
    let q = apply(T, [p[0], p[1], p[2]]);
    for (let i = 3, o = 0; i + 2 < p.length; i += 3, o += 6) {
      a.set(q, o);
      a.set(q = apply(T, [p[i], p[i + 1], p[i + 2]]), o + 3);
    }
    const old = this.lines.find(l => l.id === 'cam:' + id);
    const l = this.setLines('cam:' + id, a, [1, 1, 1, 1], old?.visible ?? false);
    l.cloud = c;
  }

  private buildOverlays(ov: Overlays) {
    const byH = new Map<number, number[]>();
    for (const s of ov.sections) {
      const a = byH.get(s.h) ?? [];
      for (const ln of s.lines) for (let i = 0; i + 3 < ln.length; i += 2) a.push(ln[i], ln[i + 1], s.h, ln[i + 2], ln[i + 3], s.h);
      byH.set(s.h, a);
    }
    for (const [h, a] of byH) this.setLines('sec' + h, new Float32Array(a), Viewer.sectionColors[String(h)] ?? [1, 1, 1, 1], false);
    const o: number[] = [], z = this.floorZ + 0.005;
    for (const s of ov.outlines) for (const lp of s.loops) {
      const n = lp.length / 2;
      for (let i = 0; i < n; i++) { const j = (i + 1) % n; o.push(lp[2 * i], lp[2 * i + 1], z, lp[2 * j], lp[2 * j + 1], z); }
    }
    if (o.length) this.setLines('outline', new Float32Array(o), [0.36, 0.6, 0.85, 1], true);
  }

  private buildGrid() {
    const [x0, y0, , x1, y1] = this.bbox.map((v, i) => (i % 3 < 2 ? (i < 3 ? Math.floor(v) - 2 : Math.ceil(v) + 2) : v));
    const mi: number[] = [], ma: number[] = [], z = this.floorZ;
    for (let x = x0; x <= x1; x++) (x % 5 ? mi : ma).push(x, y0, z, x, y1, z);
    for (let y = y0; y <= y1; y++) (y % 5 ? mi : ma).push(x0, y, z, x1, y, z);
    this.setLines('grid', new Float32Array(mi), [1, 1, 1, 0.12], false);
    this.setLines('grid5', new Float32Array(ma), [1, 1, 1, 0.3], false);
  }

  /** Place a cloud: T (an alignment, in the scene frame) on top of its published base. */
  setTransform(c: Cloud, T: Rigid) {
    c.T = compose(T, c.base);
    c.M = mat4(c.T);
    const e = this.edits.get(c.meta.id);   // its edit cloud moves with it
    if (e && e !== c) { e.T = c.T; e.M = c.M; }
    if (this.cameras[c.meta.id]) this.cameraLines(c.meta.id);
    for (const x of this.pubVox.values()) if (x.src === c) { x.vox.T = compose(c.T, x.rel); x.vox.M = mat4(x.vox.T); }   // its voxels too
    this.dirty = true;
  }

  /** Chunk centre in the scene frame. */
  wc(ch: Chunk): V3 { return apply(ch.cloud.T, ch.edit ? ch.edit.bc : ch.center); }
  /** A chunk's bounding radius (an edit chunk's covers where its frames draw it). */
  rad(ch: Chunk) { return ch.edit ? ch.edit.br : ch.radius; }

  /** An edit cloud's chunk bounds from its per-frame transforms (12 floats per frame: rows of R | t). */
  private editBounds(c: Cloud) {
    const m = c.frames?.mats;
    for (const ch of c.chunks) {
      const e = ch.edit;
      if (!e) continue;
      if (!m) { e.bc = ch.center; e.br = ch.radius; continue; }
      const cs: V3[] = e.fr.map(k => {
        const o = 12 * k, [x, y, z] = ch.center;
        return [m[o] * x + m[o + 1] * y + m[o + 2] * z + m[o + 3], m[o + 4] * x + m[o + 5] * y + m[o + 6] * z + m[o + 7], m[o + 8] * x + m[o + 9] * y + m[o + 10] * z + m[o + 11]];
      });
      if (!cs.length) { e.bc = ch.center; e.br = ch.radius; continue; }
      const bc: V3 = [0, 0, 0];
      for (const q of cs) { bc[0] += q[0] / cs.length; bc[1] += q[1] / cs.length; bc[2] += q[2] / cs.length; }
      e.bc = bc;
      e.br = ch.radius + Math.max(...cs.map(q => dist(q, bc)));
    }
  }

  // ── streaming ─────────────────────────────────────────────────────────
  private planes: number[][] = [];

  /** Decide levels wanted and issue/abort range requests. Call once per frame. */
  updateLoading() {
    if (this.lost || !this.chunks.length) return;
    const cam = this.cam, s = this.s;
    const px = cam.pxPerM * this.renderScale(false);
    const wants: Want[] = [];
    this.planes = frustumPlanes(cam.vp);
    for (const ch of this.chunks) {
      const wc = this.wc(ch);
      const visible = this.shows(ch.cloud) && sphereInFrustum(this.planes, wc, ch.radius);
      let d = 1;
      if (!cam.ortho) d = Math.max(dist(cam.eye, wc) - ch.radius, 0.3);
      ch.need = visible ? levelsWanted(ch.info.spacing ?? this.scene.spacing, d, px, s.targetPx) : 1;
      ch.priority = visible ? (ch.radius * px) / d : 0.001;
      if (visible) ch.lastUsed = this.frame;
      while (ch.levels < ch.need && !ch.pending && ch.info.levelCounts[ch.levels] === 0) { ch.levels++; this.dirty = true; }
      if (!ch.pending && ch.levels < ch.need) {
        const l = ch.levels;
        wants.push({ key: ch.key, file: ch.cloud.file, chunk: 0, level: l, offset: ch.info.offsets[l],
          bytes: ch.info.levelCounts[l] * ch.cloud.idx.record, priority: l === 0 ? 1e9 + ch.priority : ch.priority / (l + 1) });
      }
    }
    // abort requests nobody wants any more
    for (const [r, ac] of this.flight) {
      if (r.level > 0 && r.parts.every(p => this.chunks[p.key].need <= p.level)) {
        ac.abort();
        this.flight.delete(r);
        for (const p of r.parts) this.chunks[p.key].pending = false;
      }
    }
    const residents = () => this.chunks.filter(c => c.levels > 1)
      .map(c => ({ key: c.key, levels: c.levels, needed: c.need, counts: c.info.levelCounts, lastUsed: c.lastUsed, priority: c.priority }));
    // over budget (e.g. it was just lowered): drop levels nobody needs now
    if (this.loadedPts > s.budget) for (const [k, lv] of chooseEvictions(residents(), this.loadedPts, 0, s.budget, 0) ?? []) this.shrink(this.chunks[k], lv);
    const slots = 5 - this.flight.size;
    if (slots <= 0 || !wants.length) return;
    const pts = (r: RangeReq) => (r.end - r.start) / this.clouds[r.file].idx.record;
    let inFlightPts = [...this.flight.keys()].reduce((n, r) => n + pts(r), 0);
    for (const r of planRequests(wants, slots, 1.5e6)) {
      const need = pts(r);
      const ev = chooseEvictions(residents(), this.loadedPts + inFlightPts, need, s.budget, r.priority);
      if (!ev) continue;
      for (const [k, lv] of ev) this.shrink(this.chunks[k], lv);
      inFlightPts += need;
      this.fetchRange(r);
    }
  }

  /** Fetch a request's chunk levels: those in the browser cache from there, the rest in one range (then cached). */
  private fetchRange(r: RangeReq) {
    const ac = new AbortController();
    this.flight.set(r, ac);
    for (const p of r.parts) this.chunks[p.key].pending = true;
    const url = this.clouds[r.file].bin;
    (async () => {
      const { bufs, net } = await cache.fetchParts(url, r.parts, ac.signal);
      this.bytesIn += net;
      this.bytesCached += Math.max(0, r.end - r.start - net);
      return bufs;
    })()
      .then(bufs => {
        if (this.flight.get(r) !== ac) return;
        this.flight.delete(r);
        r.parts.forEach((p, i) => {
          const ch = this.chunks[p.key];
          ch.pending = false;
          if (ch.levels === p.level && !this.lost) this.append(ch, new Uint8Array(bufs[i]));
        });
        this.dirty = true;
        this.onChange();
      })
      .catch(e => {
        if (this.flight.get(r) === ac) this.flight.delete(r);
        for (const p of r.parts) this.chunks[p.key].pending = false;
        if (e.name !== 'AbortError') { console.warn(e); this.failures++; }
      });
  }
  failures = 0;
  bytesCached = 0;     // served from the browser cache instead of the network
  get inFlight() { return this.flight.size; }

  private setBuffer(ch: Chunk, cap: number) {
    const gl = this.gl, used = ch.ends[ch.levels], rs = ch.cloud.idx.record;
    const nb = gl.createBuffer()!;
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, nb);
    gl.bufferData(gl.COPY_WRITE_BUFFER, cap * rs, gl.STATIC_DRAW);
    if (ch.buf && used) {
      gl.bindBuffer(gl.COPY_READ_BUFFER, ch.buf);
      gl.copyBufferSubData(gl.COPY_READ_BUFFER, gl.COPY_WRITE_BUFFER, 0, 0, Math.min(used, cap) * rs);
    }
    if (ch.buf) gl.deleteBuffer(ch.buf);
    ch.buf = nb;
    ch.cap = cap;
    if (!ch.vao) ch.vao = gl.createVertexArray()!;
    gl.bindVertexArray(ch.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, nb);
    this.recordAttribs(rs);
    gl.bindVertexArray(null);
  }

  /** The record's attributes (0 position, 1 colour + flags, 2 normal, 5 range + edge) from the bound buffer, `rs` bytes each. */
  private recordAttribs(rs: number) {
    const gl = this.gl;
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.UNSIGNED_SHORT, true, rs, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 4, gl.UNSIGNED_BYTE, true, rs, 6);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 2, gl.UNSIGNED_BYTE, true, rs, 10);
    if (rs > 12) { gl.enableVertexAttribArray(5); gl.vertexAttribPointer(5, 2, gl.UNSIGNED_BYTE, false, rs, 12); }
  }

  private append(ch: Chunk, data: Uint8Array) {
    const gl = this.gl, rs = ch.cloud.idx.record, used = ch.ends[ch.levels], n = data.byteLength / rs;
    if (ch.cap < used + n) this.setBuffer(ch, used + n);
    gl.bindBuffer(gl.ARRAY_BUFFER, ch.buf);
    gl.bufferSubData(gl.ARRAY_BUFFER, used * rs, data);
    ch.levels++;
    this.dirty = true;
    while (ch.levels < ch.info.levelCounts.length && ch.levels < ch.need && ch.info.levelCounts[ch.levels] === 0) ch.levels++;
    this.loadedPts += n;
  }

  private shrink(ch: Chunk, levels: number) {
    this.loadedPts -= ch.ends[ch.levels] - ch.ends[levels];
    ch.levels = levels;
    this.dirty = true;
    if (ch.cap > 2 * ch.ends[levels]) this.setBuffer(ch, ch.ends[levels]);
  }

  // ── frame ─────────────────────────────────────────────────────────────
  get dpr() { return Math.min(devicePixelRatio || 1, this.s.dprCap); }
  renderScale(moving = this.moving) { return this.dpr * (moving ? this.s.moveScale : 1); }

  private fitDepth() {
    const c = this.cam, b = this.bbox;
    if (c.ortho) {
      c.planTop = b[5] + 2;
      c.planDepth = b[5] - b[2] + 4;
    } else {
      const ctr: V3 = [(b[0] + b[3]) / 2, (b[1] + b[4]) / 2, (b[2] + b[5]) / 2];
      const r = Math.hypot(b[3] - b[0], b[4] - b[1], b[5] - b[2]) / 2;
      c.far = dist(c.eye, ctr) + r + 50;
      c.near = clamp(c.far / 5000, 0.02, 0.5);
    }
  }

  private uniforms(p: Prog, pxScale: number) {
    const s = this.s, c = this.cam;
    p.m('uVP', c.vp); p.m('uV', c.view);
    p.f('uEye', ...c.e); p.f('uFwd', ...c.f);
    p.f('uPx', c.pxPerM * pxScale); p.f('uOrtho', c.ortho ? 1 : 0); p.f('uSizeMul', s.size); p.f('uGain', s.gain);
    p.i('uMode', s.mode); p.i('uSelOnly', s.selOnly && this.anySel() ? 1 : 0); p.i('uCull', s.cull ? 1 : 0); p.f('uCullT', s.cullT); p.i('uPeriod', s.period); p.i('uEdgeView', s.edgeView); p.i('uFeatView', s.featView); p.f('uDens', Math.log(this.densRange[0]), Math.log(this.densRed())); p.f('uVal', this.valRange[0], this.valRange[1]); p.f('uValCut', ...(this.valCut ?? [0, 0])); p.i('uValCutOn', this.valCut ? 1 : 0); p.f('uRangeMax', RANGE_STEP, s.rangeMax); p.f('uRamp', this.floorZ, this.floorZ + Math.max(1, this.bbox[5] - this.floorZ));
    const vols = s.volumes.slice(0, MAX_VOLUMES);
    p.f4v('uVol', new Float32Array(vols.flatMap(unitRows)));
    p.iv('uVolKind', new Int32Array(vols.map(v => KINDS.indexOf(v.kind))));
    p.i('uVolN', vols.length); p.i('uVolShow', s.volShow ? 1 : 0); p.f('uVolTint', s.volTint);
    p.i('uEditShow', s.editShow);
    p.i('uFrameT', 3);
    if (!this.clsTex) this.clsTex = this.classTexture(this.clsCols, 128);
    this.gl.activeTexture(this.gl.TEXTURE4); this.gl.bindTexture(this.gl.TEXTURE_2D, this.clsTex);
    p.i('uClsT', 4);
    if (!this.labTex) this.labTex = this.classTexture(this.labCols, 256, 5);
    this.gl.activeTexture(this.gl.TEXTURE5); this.gl.bindTexture(this.gl.TEXTURE_2D, this.labTex);
    p.i('uLabT', 5);
  }

  /** Part label tints (shaders.ts uLabT): 256 RGBA8, by the label index in aEdit's top byte. */
  private labCols: Uint8Array = new Uint8Array(256 * 4);
  private labTex: WebGLTexture | null = null;
  setLabelColors(rgba: Uint8Array) {
    this.labCols = rgba;
    if (this.labTex) this.gl.deleteTexture(this.labTex);
    this.labTex = null;
    this.dirty = true;
  }

  /** Class tints for the edit cloud (shaders.ts uClsT): 128 RGBA8, by the class index in aEdit. */
  private clsCols: Uint8Array = new Uint8Array(128 * 4);
  private clsTex: WebGLTexture | null = null;
  setClassColors(rgba: Uint8Array) {
    this.clsCols = rgba;
    if (this.clsTex) this.gl.deleteTexture(this.clsTex);
    this.clsTex = null;
    this.dirty = true;
  }
  private classTexture(rgba: Uint8Array, w: number, unit = 4) {
    const gl = this.gl, t = gl.createTexture()!;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, rgba);
    return t;
  }

  /** Bit i set when volume i is enabled and applies to the cloud. */
  private volMask(c: Cloud) {
    let m = 0;
    const id = c.meta.id, parent = c.meta.parent;   // a part / object takes its scan's boxes
    this.s.volumes.slice(0, MAX_VOLUMES).forEach((v, i) => {
      if (v.on && (!v.clouds.length || v.clouds.includes(id) || (parent && v.clouds.includes(parent)))) m |= 1 << i;
    });
    return m;
  }

  private drawPoints(p: Prog, pick = false, editOnly = false) {
    const gl = this.gl, sp = this.scene.spacing;
    const masks = new Map(this.clouds.map(c => [c, this.volMask(c)]));
    let n = 0;
    for (const ch of this.drawable()) {
      if (ch.edit && !ch.vao) this.uploadEdit(ch);
      if (!this.shows(ch.cloud) || !ch.levels || !ch.vao) continue;
      if (editOnly && !ch.edit) continue;
      if (!sphereInFrustum(this.planes, this.wc(ch), this.rad(ch))) continue;
      const lv = drawLevels(ch.levels);
      const count = ch.ends[lv];
      p.f('uOrigin', ...ch.info.origin); p.f('uSize', ...ch.info.size);
      p.f('uSpacing', ch.dispSp || (ch.info.spacing ?? sp)[lv - 1]);
      p.f('uSolid', ...ch.cloud.color);
      p.i('uVolMask', masks.get(ch.cloud)!);
      p.m('uModel', ch.cloud.M);
      const fr = ch.edit ? ch.cloud.frames : undefined;
      if (fr) {
        if (!fr.tex) fr.tex = this.frameTexture(fr.mats);
        gl.activeTexture(gl.TEXTURE3); gl.bindTexture(gl.TEXTURE_2D, fr.tex);
      }
      p.i('uFrameOn', fr ? 1 : 0);
      if (pick) p.ui('uId', ch.key + 1);
      gl.bindVertexArray(ch.vao);
      gl.drawArrays(gl.POINTS, 0, count);
      n += count;
    }
    gl.bindVertexArray(null);
    return n;
  }

  resize() {
    const c = this.canvas, d = this.dpr;
    const w = Math.max(1, Math.round(c.clientWidth * d)), h = Math.max(1, Math.round(c.clientHeight * d));
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; this.dirty = true; }
  }

  render() {
    if (this.lost || !this.scene) return;
    const gl = this.gl, c = this.cam, s = this.s;
    this.resize();
    this.frame++;
    this.fitDepth();
    c.update(this.canvas.clientWidth || 1, this.canvas.clientHeight || 1);
    this.planes = frustumPlanes(c.vp);
    this.drawnImgTris = 0;
    const rs = this.renderScale();
    const tw = Math.max(1, Math.round(c.w * rs)), th = Math.max(1, Math.round(c.h * rs));
    if (!this.tgt || this.tgt.w !== tw || this.tgt.h !== th) { this.tgt?.free(gl); this.tgt = new Target(gl, tw, th); }

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.tgt.fb);
    gl.viewport(0, 0, tw, th);
    gl.clearColor(...s.bg, 1);
    gl.depthMask(true);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LESS);
    gl.disable(gl.BLEND);
    // splat sizes follow loaded detail smoothly instead of stepping
    const now = performance.now(), dt = Math.min(0.05, (now - this.lastT) / 1000);
    this.lastT = now;
    let easing = false;
    for (const ch of this.drawable()) {
      if (!ch.levels) continue;
      const sp = ch.info.spacing ?? this.scene.spacing, t = sp[drawLevels(ch.levels) - 1];
      ch.dispSp = ch.dispSp ? ease(ch.dispSp, t, dt, 0.25) : sp[0];
      if (Math.abs(ch.dispSp - t) > 0.01 * t) easing = true; else ch.dispSp = t;
    }
    this.pts.use();
    this.uniforms(this.pts, rs);
    this.drawnPts = s.mode === 10 ? 0 : this.drawPoints(this.pts);   // Image weights: the photos alone

    // composite (+EDL) to the screen, carrying depth for the lines
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.depthFunc(gl.ALWAYS);
    const e = this.edl.use();
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, this.tgt.col);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, this.tgt.depth);
    e.i('uCol', 0); e.i('uDepth', 1);
    e.f('uTexel', 1 / tw, 1 / th); e.f('uNF', c.ortho ? 0 : c.near, c.ortho ? c.planDepth : c.far);
    e.f('uRadius', s.edlRadius * rs); e.f('uStrength', s.edlStrength); e.f('uOrtho', c.ortho ? 1 : 0);
    e.f('uEdl', s.edl ? 1 : 0); e.f('uBg', ...s.bg);
    e.f('uFill', s.fill ? (this.moving ? Math.min(2, s.fillR) : s.fillR) : 0);
    gl.bindVertexArray(this.quad);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    if (s.mode === 10) this.drawWeighted(tw, th);
    this.drawImages();

    // lines
    gl.depthFunc(gl.LEQUAL);
    gl.depthMask(false);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    const l = this.line.use();
    l.m('uVP', c.vp);
    gl.bindVertexArray(this.lineVao);
    gl.enableVertexAttribArray(0);
    for (const ls of this.lines) {
      if (!ls.visible || !ls.data.length || (this.solo !== null && ls.of !== this.solo)) continue;   // a solo cloud is shown on its own (and its lines)
      if (ls.cloud && !ls.cloud.visible) continue;
      if (ls.of !== null && this.edits.get(ls.of)?.visible === false) continue;   // an edit cloud's lines hide with it
      if (!ls.buf) {
        ls.buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, ls.buf);
        gl.bufferData(gl.ARRAY_BUFFER, ls.data, gl.STATIC_DRAW);
      }
      gl.bindBuffer(gl.ARRAY_BUFFER, ls.buf);
      gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
      if (ls.depth) gl.enable(gl.DEPTH_TEST); else gl.disable(gl.DEPTH_TEST);
      if (ls.colors && !ls.cloud) {
        if (!ls.cbuf) {
          ls.cbuf = gl.createBuffer();
          gl.bindBuffer(gl.ARRAY_BUFFER, ls.cbuf);
          gl.bufferData(gl.ARRAY_BUFFER, ls.colors, gl.STATIC_DRAW);
        }
        gl.bindBuffer(gl.ARRAY_BUFFER, ls.cbuf);
        gl.enableVertexAttribArray(1);
        gl.vertexAttribPointer(1, 4, gl.FLOAT, false, 0, 0);
      } else {
        gl.disableVertexAttribArray(1);
        const [r, g, b, a] = ls.cloud ? [...ls.cloud.color, 1] : ls.color;
        gl.vertexAttrib4f(1, r, g, b, a);
      }
      gl.drawArrays(gl.LINES, 0, ls.data.length / 3);
    }
    gl.disableVertexAttribArray(1);
    gl.bindVertexArray(null);
    gl.depthMask(true);
    gl.disable(gl.BLEND);
    this.dirty = easing;
  }
  private lastT = performance.now();

  /** Point under CSS pixel (x, y), or null. Renders a small scissored pick pass. */
  pickAt(x: number, y: number, r = 7): { p: V3; chunk: Chunk } | null {
    if (this.lost || !this.scene) return null;
    const gl = this.gl, c = this.cam;
    const w = Math.max(1, Math.round(c.w)), h = Math.max(1, Math.round(c.h));
    if (!this.ptgt || this.ptgt.w !== w || this.ptgt.h !== h) { this.ptgt?.free(gl); this.ptgt = new PickTarget(gl, w, h); }
    c.update(c.w, c.h);
    this.planes = frustumPlanes(c.vp);
    const px = Math.round(x), py = h - 1 - Math.round(y), n = 2 * r + 1;
    const x0 = clamp(px - r, 0, w - n), y0 = clamp(py - r, 0, h - n);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.ptgt.fb);
    gl.viewport(0, 0, w, h);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(x0, y0, n, n);
    gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 0]);
    gl.clearBufferfv(gl.COLOR, 1, [0, 0, 0, 0]);
    gl.clearBufferfv(gl.DEPTH, 0, [1]);
    gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LESS); gl.depthMask(true); gl.disable(gl.BLEND);
    this.pick.use();
    this.uniforms(this.pick, 1);
    this.drawPoints(this.pick, true);
    gl.disable(gl.SCISSOR_TEST);
    const a = new Uint8Array(n * n * 4), b = new Uint8Array(n * n * 4);
    gl.readBuffer(gl.COLOR_ATTACHMENT0); gl.readPixels(x0, y0, n, n, gl.RGBA, gl.UNSIGNED_BYTE, a);
    gl.readBuffer(gl.COLOR_ATTACHMENT1); gl.readPixels(x0, y0, n, n, gl.RGBA, gl.UNSIGNED_BYTE, b);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    const dv = new DataView(a.buffer), bv = new DataView(b.buffer);
    let best = -1, bd = Infinity;
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
      const k = (j * n + i) * 4;
      if (!bv.getUint32(k, true)) continue;
      const dd = (x0 + i - px) ** 2 + (y0 + j - py) ** 2;
      if (dd < bd) { bd = dd; best = k; }
    }
    if (best < 0) return null;
    const depth = dv.getFloat32(best, true), id = bv.getUint32(best, true) - 1;
    const i = (best / 4) % n, j = Math.floor(best / 4 / n);
    const sx = x0 + i + 0.5, sy = h - (y0 + j + 0.5);
    return { p: c.unproject(sx, sy, depth), chunk: this.drawable()[id] };
  }
  // ── edit cloud ────────────────────────────────────────────────────────
  /** Streamed chunks, then the edit cloud's (chunk keys index this list). */
  drawable(): Chunk[] { return this.editChunks.length ? [...this.chunks, ...this.editChunks] : this.chunks; }

  /** When set, only this edit cloud is drawn (the editor's instance overlay), with its companions ('<solo>:…', e.g. its voxels). */
  solo: string | null = null;

  /** Whether a cloud is drawn: its own toggle, and not replaced by an edit cloud (or by its voxels). */
  shows(c: Cloud) {
    if (this.solo !== null) return c.meta.id === this.solo || c.meta.id === '~ghost' || c.meta.id.startsWith(this.solo + ':');   // (a move's preview shows too)
    if (!c.visible) return false;
    if (c.file < 0) { const pv = this.pubVox.get(c.meta.id); return !pv || (this.s.voxels && this.unedited(pv.src)); }   // an edit cloud (or a published object's voxels)
    return this.unedited(c) && !(this.s.voxels && this.pubVox.has(PUB_VOX + c.meta.id));
  }
  /** A published cloud, shown and not being edited (an edit cloud of it or its scan draws instead). */
  private unedited(c: Cloud) { return c.visible && !this.edits.has(c.meta.id) && !(c.meta.parent && this.edits.has(c.meta.parent)); }

  /** Published object clouds drawn as their type's voxel model: by voxel cloud id, the object cloud and where the cells sit on it. */
  private pubVox = new Map<string, { src: Cloud; rel: Rigid; vox: Cloud }>();
  /** Does the scene have published objects drawn as voxels? */
  hasVoxels() { return this.pubVox.size > 0; }

  /**
   * Each published object of a type with a voxel model, with its fit F (an unfitted one is drawn as its points),
   * gets a voxel cloud (one decode per type) placed at the object's T · F⁻¹ · F_ref, as the editor places it.
   */
  private publishedVoxels(scene: Scene) {
    for (const id of this.pubVox.keys()) this.clearEditCloud(id);
    this.pubVox.clear();
    if (!scene.voxels) return;
    const built = new Map<string, ReturnType<typeof asCloud>>();
    for (const c of this.clouds) {
      const x = c.meta.of !== undefined ? scene.voxels[c.meta.of] : undefined, F = parseRigid(c.meta.F), Fref = parseRigid(x?.F);
      if (!x || !F || !Fref || (isIdentity(F) && c.meta.name !== x.ref)) continue;
      let b = built.get(c.meta.of!);
      if (!b) { const d = decodeVoxels(x); built.set(c.meta.of!, (b = asCloud(d.xyz, d.nrm, d.rgb, x.size))); }
      const rel = compose(inverse(F), Fref), meta = { ...c.meta, id: PUB_VOX + c.meta.id, name: `${c.meta.name} (voxels)` };
      const vox = this.setEditCloud(meta, b.idx, [b.rec], compose(c.T, rel), new Uint16Array(x.n), new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0]));
      this.pubVox.set(meta.id, { src: c, rel, vox });
    }
  }

  /**
   * Draw scan `meta` from fully loaded records instead of its streamed clouds.
   * recs[i] holds chunk i's records level by level (vertex order); T places it.
   */
  setEditCloud(meta: CloudMeta, idx: CloudIndex, recs: Uint8Array[], T: Rigid, frameIx: Uint16Array, frameMats: Float32Array) {
    this.clearEditCloud(meta.id);
    const cloud: Cloud = { meta, idx, bin: '', chunks: [], visible: true, color: hex2rgb(meta.color), file: -1, T, M: mat4(T), base: identity(),
      frames: { mats: frameMats, tex: null } };
    let o = 0;
    idx.chunks.forEach((info, i) => {
      const nv = recs[i].length / RECORD;
      const ends = [0];
      for (const n of info.levelCounts) ends.push(ends[ends.length - 1] + n);
      const ch: Chunk = {
        key: 0, cloud, info, ends, center: [0, 1, 2].map(k => info.origin[k] + info.size[k] / 2) as V3,
        radius: Math.hypot(...info.size) / 2, levels: info.levelCounts.length, pending: false, need: info.levelCounts.length,
        draw: 0, lastUsed: 0, priority: 0, buf: null, vao: null, cap: 0, dispSp: 0,
        edit: { rec: recs[i], state: new Uint32Array(nv), sbuf: null, fix: frameIx.subarray(o, o + nv), fbuf: null, dens: new Uint16Array(nv), dbuf: null, feat: new Uint8Array(nv), ebuf: null, val: new Float32Array(nv).fill(NO_VAL), vbuf: null, fr: [...new Set(frameIx.subarray(o, o + nv))], bc: [0, 0, 0], br: 0 },
      };
      o += nv;
      cloud.chunks.push(ch);
      this.editChunks.push(ch);
    });
    this.editBounds(cloud);
    this.edits.set(meta.id, cloud);
    this.rekey();
    return cloud;
  }

  /** New per-frame transforms (the alignment tree) for one scan's edit cloud. */
  setEditFrames(id: string, mats: Float32Array) {
    const c = this.edits.get(id);
    if (!c?.frames) return;
    if (c.frames.tex) this.gl.deleteTexture(c.frames.tex);
    c.frames = { mats, tex: null };
    this.editBounds(c);
    this.dirty = true;
  }

  /** RGBA32F, 3 texels per frame: rows of (R | t). */
  private frameTexture(mats: Float32Array) {
    const gl = this.gl, t = gl.createTexture()!;
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, 3, mats.length / 12, 0, gl.RGBA, gl.FLOAT, mats);
    return t;
  }

  /** Stop drawing one scan's edit cloud (or all of them: the editor's, not the published objects' voxels). */
  clearEditCloud(id?: string) {
    if (id === undefined) { for (const k of [...this.edits.keys()]) if (!this.pubVox.has(k)) this.clearEditCloud(k); return; }
    const gl = this.gl;
    for (const ch of this.editChunks) {
      if (ch.cloud.meta.id !== id) continue;
      if (ch.buf) gl.deleteBuffer(ch.buf);
      if (ch.edit!.sbuf) gl.deleteBuffer(ch.edit!.sbuf);
      if (ch.edit!.fbuf) gl.deleteBuffer(ch.edit!.fbuf);
      if (ch.edit!.dbuf) gl.deleteBuffer(ch.edit!.dbuf);
      if (ch.edit!.vbuf) gl.deleteBuffer(ch.edit!.vbuf);
      if (ch.edit!.ebuf) gl.deleteBuffer(ch.edit!.ebuf);
      if (ch.vao) gl.deleteVertexArray(ch.vao);
    }
    const c = this.edits.get(id);
    if (c?.frames?.tex) gl.deleteTexture(c.frames.tex);
    this.editChunks = this.editChunks.filter(ch => ch.cloud.meta.id !== id);
    this.edits.delete(id);
    this.rekey();
  }

  /** Selection only draws everything while nothing is selected. */
  private anySel() { for (const c of this.edits.values()) if (c.sel) return true; return false; }

  /** Edit chunks' keys follow the streamed ones (pick ids index drawable()). */
  private rekey() {
    this.editChunks.forEach((ch, i) => { ch.key = this.chunks.length + i; });
    this.dirty = true;
  }

  /** New per-vertex edit state for one scan's edit cloud (concatenated in chunk order). */
  setEditState(id: string, words: Uint32Array) {
    const gl = this.gl, c = this.edits.get(id);
    if (!c) return;
    c.sel = words.some(w => (w & 2) !== 0);
    let o = 0;
    for (const ch of c.chunks) {
      const e = ch.edit!, n = e.state.length;
      e.state.set(words.subarray(o, o + n));
      o += n;
      if (e.sbuf) { gl.bindBuffer(gl.ARRAY_BUFFER, e.sbuf); gl.bufferSubData(gl.ARRAY_BUFFER, 0, e.state); }
    }
    this.dirty = true;
  }

  /** Each point's density (points within s.densR, 0: not measured) for one scan's edit cloud, in vertex order; null clears it. */
  setEditDensity(id: string, d: Uint16Array | null) {
    const gl = this.gl, c = this.edits.get(id);
    if (!c) return;
    let o = 0;
    for (const ch of c.chunks) {
      const e = ch.edit!, n = e.dens.length;
      if (d) e.dens.set(d.subarray(o, o + n)); else e.dens.fill(0);
      o += n;
      if (e.dbuf) { gl.bindBuffer(gl.ARRAY_BUFFER, e.dbuf); gl.bufferSubData(gl.ARRAY_BUFFER, 0, e.dens); }
    }
    this.dirty = true;
  }

  /** Each point's value in the data group shown (Data shading; NaN: not in it) for one scan's edit cloud, in vertex order; null: none in it. */
  setEditValues(id: string, d: Float32Array | null) {
    const gl = this.gl, c = this.edits.get(id);
    if (!c) return;
    let o = 0;
    for (const ch of c.chunks) {
      const e = ch.edit!, n = e.val.length;
      if (d) for (let i = 0; i < n; i++) { const x = d[o + i]; e.val[i] = x === x ? x : NO_VAL; }
      else e.val.fill(NO_VAL);
      o += n;
      if (e.vbuf) { gl.bindBuffer(gl.ARRAY_BUFFER, e.vbuf); gl.bufferSubData(gl.ARRAY_BUFFER, 0, e.val); }
    }
    this.dirty = true;
  }

  /** Each point's image feature marks (photo/featshade.ts; 0: unknown) for one scan's edit cloud, in vertex order; null clears them. */
  setEditFeatures(id: string, d: Uint8Array | null) {
    const gl = this.gl, c = this.edits.get(id);
    if (!c) return;
    let o = 0;
    for (const ch of c.chunks) {
      const e = ch.edit!, n = e.feat.length;
      if (d) e.feat.set(d.subarray(o, o + n)); else e.feat.fill(0);
      o += n;
      if (e.ebuf) { gl.bindBuffer(gl.ARRAY_BUFFER, e.ebuf); gl.bufferSubData(gl.ARRAY_BUFFER, 0, e.feat); }
    }
    this.dirty = true;
  }

  private uploadEdit(ch: Chunk) {
    const gl = this.gl, e = ch.edit!;
    ch.buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, ch.buf);
    gl.bufferData(gl.ARRAY_BUFFER, e.rec, gl.STATIC_DRAW);
    ch.cap = e.rec.length / RECORD;
    e.sbuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, e.sbuf);
    gl.bufferData(gl.ARRAY_BUFFER, e.state, gl.DYNAMIC_DRAW);
    ch.vao = gl.createVertexArray()!;
    gl.bindVertexArray(ch.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, ch.buf);
    this.recordAttribs(RECORD);   // edit records are always RECORD long (session.ts widens a version 1 build's)
    gl.bindBuffer(gl.ARRAY_BUFFER, e.sbuf);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribIPointer(3, 1, gl.UNSIGNED_INT, 0, 0);
    e.fbuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, e.fbuf);
    gl.bufferData(gl.ARRAY_BUFFER, e.fix, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(4);
    gl.vertexAttribIPointer(4, 1, gl.UNSIGNED_SHORT, 0, 0);
    e.dbuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, e.dbuf);
    gl.bufferData(gl.ARRAY_BUFFER, e.dens, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(6);
    gl.vertexAttribPointer(6, 1, gl.UNSIGNED_SHORT, false, 0, 0);
    e.ebuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, e.ebuf);
    gl.bufferData(gl.ARRAY_BUFFER, e.feat, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(7);
    gl.vertexAttribIPointer(7, 1, gl.UNSIGNED_BYTE, 0, 0);
    e.vbuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, e.vbuf);
    gl.bufferData(gl.ARRAY_BUFFER, e.val, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(8);
    gl.vertexAttribPointer(8, 1, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
  }

  /**
   * View depth (m) of the edit cloud's front surface over a CSS-pixel rect
   * (0 = no point there), for front-only selections. Row 0 is the rect's top.
   */
  editDepth(x: number, y: number, w: number, h: number): { x: number; y: number; w: number; h: number; d: Float32Array } | null {
    if (this.lost || !this.edits.size) return null;
    const gl = this.gl, c = this.cam;
    const W = Math.max(1, Math.round(c.w)), Hh = Math.max(1, Math.round(c.h));
    if (!this.ptgt || this.ptgt.w !== W || this.ptgt.h !== Hh) { this.ptgt?.free(gl); this.ptgt = new PickTarget(gl, W, Hh); }
    c.update(c.w, c.h);
    this.planes = frustumPlanes(c.vp);
    const x0 = clamp(Math.floor(x), 0, W - 1), y1 = clamp(Math.floor(y), 0, Hh - 1);
    const rw = clamp(Math.ceil(w), 1, W - x0), rh = clamp(Math.ceil(h), 1, Hh - y1);
    const gy = Hh - y1 - rh;   // GL rows count from the bottom
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.ptgt.fb);
    gl.viewport(0, 0, W, Hh);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(x0, gy, rw, rh);
    gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 0]);
    gl.clearBufferfv(gl.COLOR, 1, [0, 0, 0, 0]);
    gl.clearBufferfv(gl.DEPTH, 0, [1]);
    gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LESS); gl.depthMask(true); gl.disable(gl.BLEND);
    this.pick.use();
    this.uniforms(this.pick, 1);
    this.drawPoints(this.pick, true, true);
    gl.disable(gl.SCISSOR_TEST);
    const a = new Uint8Array(rw * rh * 4);
    gl.readBuffer(gl.COLOR_ATTACHMENT0); gl.readPixels(x0, gy, rw, rh, gl.RGBA, gl.UNSIGNED_BYTE, a);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    const dv = new DataView(a.buffer), d = new Float32Array(rw * rh);
    for (let j = 0; j < rh; j++) for (let i = 0; i < rw; i++) d[(rh - 1 - j) * rw + i] = dv.getFloat32((j * rw + i) * 4, true);
    return { x: x0, y: y1, w: rw, h: rh, d };
  }

}
